import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { CheckCircle2 } from 'lucide-react';
import { EcointelLottieMark } from '../components/EcoIntelLoader';
import { verifyCheckoutSession } from '../api';

export default function Success() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying');
  const verifyAttempted = useRef(false);

  useEffect(() => {
    if (!auth.currentUser) {
      setStatus('error');
      return;
    }

    const tryVerifyFromStripe = async () => {
      if (!sessionId || verifyAttempted.current) return;
      verifyAttempted.current = true;
      try {
        await verifyCheckoutSession(sessionId);
      } catch (e) {
        console.warn('verifyCheckoutSession:', e?.message || e);
      }
    };

    tryVerifyFromStripe();

    const unsub = onSnapshot(doc(db, 'users', auth.currentUser.uid), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();

      if (data.subscriptionStatus === 'active' || data.subscriptionOverride) {
        setStatus('activated');
        setTimeout(() => navigate('/dashboard'), 2500);
      }
    });

    const timeout = setTimeout(() => {
      setStatus((prev) => (prev === 'verifying' ? 'slow' : prev));
    }, 10000);

    return () => {
      unsub();
      clearTimeout(timeout);
    };
  }, [navigate, sessionId]);

  return (
    <div className="flex flex-col items-center justify-center h-full bg-slate-50">
      <div className="bg-white p-10 rounded-2xl shadow-sm border border-slate-200 text-center max-w-md">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
          status === 'activated' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'
        }`}>
          {status === 'activated' ? <CheckCircle2 size={32} /> : <EcointelLottieMark size={36} />}
        </div>

        {status === 'verifying' && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Received!</h1>
            <p className="text-slate-500">Activating your subscription...</p>
          </>
        )}

        {status === 'activated' && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Welcome, Founding Associate!</h1>
            <p className="text-slate-500 mb-4">Your subscription is now active. Redirecting to your dashboard...</p>
            <div className="flex items-center justify-center gap-2 text-sm font-bold text-green-700 bg-green-50 py-3 rounded-lg">
              <CheckCircle2 size={16} /> All features unlocked
            </div>
          </>
        )}

        {status === 'slow' && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Received!</h1>
            <p className="text-slate-500 mb-4">
              Activation is taking a moment. This usually completes within a few seconds.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500 bg-slate-100 py-3 rounded-lg">
              <EcointelLottieMark size={18} /> Still processing...
            </div>
            <p className="text-xs text-slate-400 mt-4">
              You can open your dashboard when you&apos;re ready. If your subscription still shows as inactive, wait a
              minute and refresh your profile, or contact support with your payment receipt.
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="mt-4 text-sm text-[#043132] hover:underline font-medium"
            >
              Go to dashboard anyway
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h1>
            <p className="text-slate-500 mb-4">
              We couldn&apos;t verify your subscription. Please log in and check your profile.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="px-6 py-2.5 bg-[#043132] text-white font-medium rounded-xl hover:bg-[#065354]"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
